"use client";
import { AuthCallbackBoundary } from "@jetblue/kit-auth-react";
import { useRouter } from "next/navigation";
import { useCallback } from "react";

const FALLBACK = "/";
const SAFE_URL_CHARS = /^[A-Za-z0-9\-._~!$&'()*+,;=:@%/?#[\]]*$/;

function sanitizeReturnTo(returnTo?: string | null): string {
  if (typeof returnTo !== "string" || returnTo.length === 0) {
    return FALLBACK;
  }
  if (returnTo.length > 1024) {
    return FALLBACK;
  }
  if (!SAFE_URL_CHARS.test(returnTo)) {
    return FALLBACK;
  }
  if (!returnTo.startsWith("/") || returnTo.startsWith("//")) {
    return FALLBACK;
  }

  const base = globalThis.location.origin;
  if (!base || base === "null") {
    return FALLBACK;
  }

  let url: URL;
  try {
    url = new URL(returnTo, base);
  } catch {
    return FALLBACK;
  }
  if (url.origin !== base) {
    return FALLBACK;
  }

  return `${url.pathname}${url.search}${url.hash}`;
}

export const KitAuthLoginCallback = () => {
  const router = useRouter();
  const onComplete = useCallback(
    ({ returnTo }: { returnTo?: string }) => {
      router.replace(sanitizeReturnTo(returnTo));
    },
    [router],
  );
  const onError = useCallback(() => {
    router.replace("/error");
  }, [router]);

  return (
    <AuthCallbackBoundary
      fallback={<></>}
      onComplete={onComplete}
      onError={onError}
    >
      {null}
    </AuthCallbackBoundary>
  );
};
